import { useState, useEffect, useContext } from "react";
import useApi from "../../../../../utils/api-manager/Helper/useApi";
import AuthContext from "../../../../../utils/secure-route/AuthContext";
import { useToastHandler } from "../../../../common/ToastHandler";

const ManageStockController = () => {
  const [productList, setProductList] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const { user } = useContext(AuthContext);
  const storeId = user.store;
  const [barcode, setBarcode] = useState("");
  const [category, setCategory] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [sellingPrice, setSellingPrice] = useState("");
  const [uom, setUom] = useState("");
  const [totalPcInUom, setTotalPcInUom] = useState("");
  const [currentStock, setCurrentStock] = useState("");
  const [totalStockArrived, setTotalStockArrived] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isNewProduct, setIsNewProduct] = useState(false);
  const [productTitle, setProductTitle] = useState("");
  const [openingInventory, setOpeningInventory] = useState("0");
  // Optional - only perishable stock needs this. Left blank means "no
  // expiry", same as any other product.
  const [expiryDate, setExpiryDate] = useState("");
  const [reloadData, setReloadData] = useState(false);
  const [inventoryData, setInventoryData] = useState([]);
  const [purchasePriceError, setPurchasePriceError] = useState("");
  const [sellingPriceError, setSellingPriceError] = useState("");

  const [vendors, setVendors] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState("");
  const [selectedVendorName, setSelectedVendorName] = useState("");
  const [showVendorModal, setShowVendorModal] = useState(false);

  const [segments, setSegments] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [uomDataStore, ] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedProductSearch, setDebouncedProductSearch] = useState("");
  const [selectedUom, setSelectedUom] = useState("");
  const [segmentId, setSegmentId] = useState("");
  const [selectedCategory, setSelectedCategory] = useState([]);

  const [autoGenerateBarcode, setAutoGenerateBarcode] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedInventorySearch, setDebouncedInventorySearch] = useState("");

  const { Get, Post, getAPI } = useApi();
  const {handleToast} = useToastHandler()

  useEffect(() => {
    Get("vendorDepartmentData").then((res) => {
      const list = res?.results || res?.data || (Array.isArray(res) ? res : []);
      setVendors(list);
    }).catch((err) => console.error("Failed to fetch vendors:", err));
  }, []);

  const handleOpenModal = () => {
    setShowModal(true);
    fetchProductList("");
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
    setProductList([]);
    setSearchQuery("");
    setDebouncedProductSearch("");
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedProductSearch(searchQuery);
    }, 500); 
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedInventorySearch(searchTerm);
    }, 500); 
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    const trimmed = debouncedProductSearch.trim();
    if (trimmed === "" || trimmed.length >= 2) {
      fetchProductList(trimmed);
    }
  }, [debouncedProductSearch]);

  useEffect(() => {
    fetchInventoryData(currentPage, debouncedInventorySearch);
  }, [currentPage, reloadData, debouncedInventorySearch]);

  const fetchProductList = async (searchQuery = "") => {
    const trimmed = searchQuery.trim();

    if (trimmed !== "" && trimmed.length < 2) {
      setProductList([]);
      return;
    }

    setLoading(true);
    try {
      const apiUrl = `${getAPI("getProductAndInventory")}?search=${trimmed}`;
      const response = await Post(apiUrl);
      setProductList(response.data.results || []);
    } catch (error) {
      console.error("Failed to fetch product list:", error);
      handleToast({
        type:'ERROR',
        message:'Failed to fetch product list:' + error
      })
      setProductList([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchInventoryData = (page, search = "") => {
    setLoading(true);
    let apiUrl = `${getAPI("showProductAndInventory")}?page=${page}`;
    if (search && search.trim()) {
      apiUrl += `&search=${encodeURIComponent(search.trim())}`;
    } 

    Post(apiUrl)
      .then((response) => {
        setInventoryData(response.data.results);
        setTotalCount(response.data.count);
      })
      .catch((error)=> {
        console.error("Failed to fetch  product list:", error);
        handleToast({
          type:'ERROR',
          message:'Failed to fetch  product list:' + error
        })
        setInventoryData([]);
        setTotalCount(0);
      })
      .finally (() => {
        setLoading(false);
      });
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1); 
  };

  const handleDataUpdate = () => {
    fetchInventoryData(currentPage, debouncedInventorySearch);
  };

  useEffect(() => {
    if (selectedProduct) {
      fetchProductDetails();
    }
  }, [selectedProduct]);

  const handleVendorChange = (e) => {
    const vendorId = e.target.value;

    if (vendorId === "add-vendor") {
      setShowVendorModal(true);
      return;
    }

    if (vendorId === "") {
      setSelectedVendorId("");
      setSelectedVendorName("");
      return;
    }

    const selectedVendor = vendors.find((vendor) => vendor.id === vendorId);
    if (selectedVendor) {
      setSelectedVendorId(selectedVendor.id);
      setSelectedVendorName(selectedVendor.name);
    }
  };

  const handleCloseVendorModal = () => {
    setShowVendorModal(false);
  };

  const updateVendors = (newVendor) => {
    setVendors((prev) => [...prev, newVendor]);
  };

  const handleVendorAdded = async (newVendor) => {
    try {
      setSelectedVendorId(newVendor.id);
      setSelectedVendorName(newVendor.name);
      setShowVendorModal(false);
    } catch (error) {
      console.error("Error handling new vendor:", error);
      handleToast({
        type:'ERROR',
        message:"Error updating vendors list"
      })
    }
  };

  const handleProductTitleChange = (e) => {
    const value = e.target.value;
    setProductTitle(value.charAt(0).toUpperCase() + value.slice(1));
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    setCategory(value.charAt(0).toUpperCase() + value.slice(1));
  };

  const handleOpeningInventoryChange = (e) => {
    const { value } = e.target;
    if (value.startsWith('0') && value.length > 1) {
      setOpeningInventory(value.slice(1));
      return;
    }
    if (value.length <= 10) {
      setOpeningInventory(value);
    }
  };

  const handleFocusOpeningInventory = () => {
    if (openingInventory === "0") {
      setOpeningInventory("");
    }
  };

  const handleBlurOpeningInventory = () => {
    if (openingInventory === "") {
      setOpeningInventory("0");
    }
  }

  const handleTotalPcInUomChange = (e) => setTotalPcInUom(e.target.value);
  const handleBarcodeChange = (e) => setBarcode(e.target.value);

  const handlePurchasePriceChange = (e) => {
    const { value } = e.target;
    if (value.length <= 10) {
      setPurchasePrice(value);
      setPurchasePriceError("");
    }
  };

  const handleSellingPriceChange = (e) => {
    const { value } = e.target;
    if (value.length <= 10) {
      setSellingPrice(value);
      setSellingPriceError("");
    }
  };

  const handleUomChange = (e) => {
    const selectedValue = e.target.value;

    if (!selectedValue) {
      setUom("");
      setPurchasePrice("");
      setSellingPrice("");
      setTotalPcInUom("");
      setSelectedSegment(null);
      setSegmentId("");
      setCurrentStock("");
      return;
    }

    const [selectedUom, selectedPc] = selectedValue.split("_");

    const matchingSegment = segments.find(
      (seg) =>
        seg.uom_title === selectedUom &&
        seg.total_pc_in_uom === parseInt(selectedPc)
    );

    if (matchingSegment) {
      setUom(matchingSegment.uom);
      setPurchasePrice(matchingSegment.purchase_price.toString());
      setSellingPrice(matchingSegment.selling_price.toString());
      setTotalPcInUom(matchingSegment.total_pc_in_uom);
      setSelectedSegment(matchingSegment);
      setBarcode(matchingSegment.segment_barcode);
      setSegmentId(matchingSegment.id);
      setCurrentStock(matchingSegment.remaining_inventory ?? 0);
    }
  };

  const handleSellingPriceBlur = () => {
    if (parseFloat(sellingPrice) < parseFloat(purchasePrice)) {
      setSellingPriceError("Selling price cannot be less than purchase price.");
    } else {
      setSellingPriceError("");
    }
  };

  const handleConfirmNewProduct = () => {
    setShowConfirmation(false);
    setIsNewProduct(true);

    setSelectedProduct(null);
    setProductTitle("");
    setPurchasePrice("");
    setSellingPrice("");
    setCategory("");
    setUom("");
    setCurrentStock("0");
    setSelectedCategory([]);
    setAutoGenerateBarcode(false);
    setSelectedUom(null);
    setSegments([]);
    setSelectedSegment(null);
    setTotalStockArrived("");
    setOpeningInventory("0");
    setExpiryDate("");
  };

  const handleCancelNewProduct = () => {
    setShowConfirmation(false);
    resetForm();
  };

  const scanBarcode = async () => {
    if (!barcode) return;

    try {
      const requestData = { segment_barcode: barcode };
      const response = await Post("showProductAndInventory", requestData);

      if (response.data) {
        const product = response.data;
        setSelectedProduct({
          id: product.id,
          title: product.title,
        });

        setCategory(product.category || "");
        setProductTitle(product.title);

        if (product.segments && product.segments.length > 0) {
          setSegments(product.segments);
          const firstSegment = product.segments[0];

          setUom(firstSegment.uom_title);
          setPurchasePrice(firstSegment.purchase_price?.toString() || "0");
          setSellingPrice(firstSegment.selling_price?.toString() || "0");
          setTotalPcInUom(firstSegment.total_pc_in_uom);
          setSelectedSegment(firstSegment);
          setCurrentStock(firstSegment.remaining_inventory ?? 0);
        }

        setTotalStockArrived("");
        if (product.vendor) {
          const vendorId =
            typeof product.vendor === "string"
              ? product.vendor
              : product.vendor.toString();
          setSelectedVendorId(vendorId);
          setSelectedVendorName(product.vendor_name || "");
        }
      } else {
        setShowConfirmation(true);
      }
    } catch (error) {
      setShowConfirmation(true);
    }
  };

  const fetchProductDetails = async () => {
    if (!selectedProduct) return;

    try {
      const requestData = {
        product_id: selectedProduct.id,
      };
      const response = await Post("showProductAndInventory", requestData);
      const product = response.data;
      if (product) {
        setBarcode(product.segments.segment_barcode);
        setCategory(product.category);
        setSegmentId(product.segments.id);

        if (product.segments && product.segments.length > 0) {
          setSegments(product.segments);
          const firstSegment = product.segments[0];

          setSegmentId(firstSegment.id);
          setUom(firstSegment.uom);
          setPurchasePrice(firstSegment.purchase_price?.toString() || "0");
          setSellingPrice(firstSegment.selling_price?.toString() || "0");
          setTotalPcInUom(firstSegment.total_pc_in_uom);
          setBarcode(firstSegment.segment_barcode);
          setSelectedSegment(firstSegment);
          setCurrentStock(firstSegment.remaining_inventory ?? 0);
        }

        setTotalStockArrived("");
        if (product.vendor) {
          const vendorId =
            typeof product.vendor === "string"
              ? product.vendor
              : product.vendor.toString();
          setSelectedVendorId(vendorId);
          setSelectedVendorName(product.vendor_name || "");
        }
      } else {
        handleToast({
          type:'WARNING',
          message:"No product details found for the selected product."
        })
      }
    } catch (error) {
      console.error("Failed to fetch product details");
      handleToast({
        type:'ERROR',
        message:"Failed to fetch product details"
      })
    }
  };

  const resetForm = () => {
    setSelectedProduct("");
    setBarcode("");
    setCategory("");
    setTotalPcInUom("");
    setPurchasePrice("");
    setSellingPrice("");
    setCurrentStock("");
    setTotalStockArrived("");
    setIsNewProduct(false);
    setProductTitle("");
    setOpeningInventory("0");
    setExpiryDate("");
    setSellingPriceError("");
    setPurchasePriceError("");
    setShowConfirmation(false);
    setSelectedVendorId("");
    setSelectedVendorName("");
    setSelectedUom("");
    setSelectedCategory([]);
    setAutoGenerateBarcode(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (parseFloat(sellingPrice) < parseFloat(purchasePrice)) {
      handleToast({
        type:'ERROR',
        message:"Selling price cannot be less than the purchase price."
      })
      return;
    }

    try {
      setIsSubmitting(true);
      if (isNewProduct) {
        const productData = {
          store: storeId,
          title: productTitle,
          barcode: autoGenerateBarcode ? undefined : barcode,
          categories: selectedCategory.map(c => c.id),
          uom: selectedUom?.id,
          total_pc_in_uom: totalPcInUom,
          purchase_price: purchasePrice,
          selling_price: sellingPrice,
          opening_inventory: openingInventory,
          vendor: selectedVendorId,
          vendor_name: selectedVendorName,
          expiry_date: expiryDate || null,
        };

        const productResponse = await Post("productData", productData);
        if (productResponse?.data?.id) {
          handleToast({
            type:'SUCCESS',
            message:"New product created successfully!"
          })
          setReloadData(!reloadData);
        }
      } else if (selectedProduct) {
        const inventoryData = {
          store: storeId,
          product:
            typeof selectedProduct === "object"
              ? selectedProduct.id
              : selectedProduct,
          date: new Date().toISOString(),
          category: category,
          uom: uom,
          total_pc_in_uom: totalPcInUom,
          purchase_price: parseFloat(purchasePrice).toFixed(2),
          selling_price: parseFloat(sellingPrice).toFixed(2),
          total_stock_arrived: totalStockArrived || 0,
          vendor: selectedVendorId,
          vendor_name: selectedVendorName,
          segment: segmentId,
          expiry_date: expiryDate || null,
        };

        await Post("productInventoryData", inventoryData);

        handleToast({
          type:'SUCCESS',
          message:"Stock added successfully!"
        })
        setReloadData(!reloadData);
      } else {
        handleToast({
          type:'ERROR',
          message:"Please select a product or scan a barcode."
        })
        return;
      }

      handleCloseModal();
      resetForm();
    } catch (error) {
      handleToast({
        type: 'ERROR',
        message: `${error.response?.data?.message || "Error saving product details."}`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // useEffect(() => {
  //   const fetchUomData = async () => {
  //     try {
  //       const response = await Get("uomData");
  //       if (response && response.results) {
  //         setUomDataStore(response.results);
  //       }
  //     } catch (error) {
  //       console.error("Error fetching UOM data:", error);
  //     }
  //   };

  //   fetchUomData();
  // }, []);

  return {
    productList,
    selectedProduct,
    barcode,
    category,
    purchasePrice,
    sellingPrice,
    uom,
    vendors,
    uomDataStore,
    currentStock,
    totalStockArrived,
    isNewProduct,
    setIsNewProduct,
    showConfirmation,
    productTitle,
    openingInventory,
    setOpeningInventory,
    expiryDate,
    setExpiryDate,
    setSelectedProduct,
    setTotalStockArrived,
    setProductTitle,
    setCategory,
    setUom,
    setPurchasePrice,
    setSellingPrice,
    handleSubmit,
    handleConfirmNewProduct,
    handleCancelNewProduct,
    resetForm,
    scanBarcode,
    setBarcode,
    handleBarcodeChange,
    handleCategoryChange,
    handleOpenModal,
    handleCloseModal,
    showModal,
    inventoryData,
    handleProductTitleChange,
    handleUomChange,
    purchasePriceError,
    sellingPriceError,
    handleSellingPriceBlur,
    handlePurchasePriceChange,
    handleSellingPriceChange,
    vendors,
    selectedVendorId,
    selectedVendorName,
    showVendorModal,
    handleVendorChange,
    handleCloseVendorModal,
    updateVendors,
    handleVendorAdded,
    totalPcInUom,
    handleTotalPcInUomChange,
    segments,
    selectedSegment,
    handleOpeningInventoryChange,
    handleFocusOpeningInventory,
    handleBlurOpeningInventory,
    currentPage,
    totalCount,
    loading,
    isSubmitting,
    handlePageChange,
    handleDataUpdate,
    fetchProductList,
    searchQuery,
    setSearchQuery,
    setProductList,
    selectedUom,
    setSelectedUom,
    searchTerm,
    handleSearch,
    selectedCategory,
    setSelectedCategory,
    autoGenerateBarcode,
    setAutoGenerateBarcode,
    selectedVendorName,
    setSelectedVendorName,
  };
};

export default ManageStockController;
